import { useEffect, useRef, useState } from "react";

/**
 * Fade a piece in ONCE as it arrives, then leave it fully visible.
 *
 * This deliberately does NOT dim content continuously as you scroll. Three
 * earlier attempts did, and all three were wrong for the same underlying
 * reason: they treated live text as decoration.
 *
 *   v1  opacity from distance-to-viewport-centre — anything taller than the
 *       screen sat permanently dimmed while being read. Stories here run to
 *       5,228px against a ~900px viewport.
 *   v2  opacity from viewport overlap — correct maths, still dimming.
 *   v3  the scroll listener was bound to `window`, but this page scrolls inside
 *       a div, so it never fired and spreads froze at whatever value they had
 *       on entry. That is the grey Sam saw.
 *
 * Beyond the bugs it was the wrong idea: body text at 35% opacity on cream
 * fails contrast, and this readership skews older. LANDMARKS-PREVIEW.html — the
 * version that was approved — fades each place in on arrival and then leaves it
 * at full opacity. That is what this does.
 *
 * The trigger is `threshold: 0` with a negative rootMargin, NOT a threshold on
 * the element's own area. A threshold is a fraction of the ELEMENT, so the
 * ceiling any tall piece can reach is viewport-height / element-height. The
 * longest spread here ("E.I. In Love | Letter No. 3") renders around 6,200px,
 * which on a 620px phone tops out at 0.099 — it cleared the old 0.08 threshold
 * by a quarter, and one longer article from Blair would have pushed it under and
 * left that story permanently invisible, with nothing logged anywhere. Any
 * overlap with a shrunken viewport behaves identically at every height.
 *
 * Returns [ref, opacity, approached]. `approached` latches true the first time
 * the element nears the screen, so callers can lazily fetch heavy content.
 */
export default function useScrollFade() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [approached, setApproached] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // No observer, or motion is unwanted: show and load everything. Content is
    // never withheld by an effect that could not run.
    if (reduced || typeof IntersectionObserver === "undefined") {
      setShown(true);
      setApproached(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setApproached(true);
          setShown(true);
          io.unobserve(entry.target); // once is enough — it never fades back out
        });
      },
      { threshold: 0, rootMargin: "-6% 0px -6% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, shown ? 1 : 0, approached];
}
